import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../../services/firestoreService';

const ManageSales = () => {
  const [meta, setMeta] = useState('');
  const [period, setPeriod] = useState('');
  const [budgetId, setBudgetId] = useState('');
  const [amount, setAmount] = useState('');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingSale, setEditingSale] = useState(null);

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      const salesData = await getCollection('Sales');
      // Ordenar por fecha de creación descendente
      const sortedSales = salesData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA;
      });
      setSales(sortedSales);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const compositeId = `${meta.trim()}::${budgetId.trim()}`;

      const saleData = {
        meta: meta.trim(),
        period: period,
        budgetId: budgetId.trim(),
        amount: amount ? parseFloat(amount) : 0,
        rawAmount: amount,
        compositeId: compositeId,
        createdAt: editingSale ? editingSale.createdAt : new Date(),
        updatedAt: new Date()
      };

      if (editingSale) {
        await updateDocument('Sales', editingSale.id, saleData);
        setSnackbar({
          open: true,
          message: 'Venta actualizada exitosamente',
          severity: 'success'
        });
        setEditingSale(null);
      } else {
        await addDocument('Sales', saleData);
        setSnackbar({
          open: true,
          message: 'Venta creada exitosamente',
          severity: 'success'
        });
      }

      // Limpiar formulario
      setMeta('');
      setPeriod('');
      setBudgetId('');
      setAmount('');

      // Actualizar lista
      fetchSales();
    } catch (error) {
      console.error('Error saving sale:', error);
      setSnackbar({
        open: true,
        message: 'Error al guardar la venta',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setMeta(sale.meta || '');
    setPeriod(sale.period || '');
    setBudgetId(sale.budgetId || '');
    setAmount(sale.amount ? sale.amount.toString() : '');
  };

  const handleCancelEdit = () => {
    setEditingSale(null);
    setMeta('');
    setPeriod('');
    setBudgetId('');
    setAmount('');
  };

  const handleDelete = async (saleId) => {
    if (window.confirm('¿Estás seguro de eliminar esta venta?')) {
      try {
        await deleteDocument('Sales', saleId);
        setSnackbar({
          open: true,
          message: 'Venta eliminada exitosamente',
          severity: 'success'
        });
        fetchSales();
      } catch (error) {
        console.error('Error deleting sale:', error);
        setSnackbar({
          open: true,
          message: 'Error al eliminar la venta',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatPeriod = (period) => {
    if (!period) return '-';
    // Formato esperado: YYYY-MM
    const [year, month] = period.split('-');
    if (!year || !month) return period;
    
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    const monthIndex = parseInt(month, 10) - 1;
    return `${months[monthIndex]} ${year}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date?.toDate?.() || new Date(date);
    return d.toLocaleDateString('es-CL');
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Gestionar Ventas
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Administra las ventas reales del sistema
      </Typography>
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default' }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Meta"
                value={meta}
                onChange={(e) => setMeta(e.target.value)}
                required
                placeholder="Ej: Meta mensual de solicitudes"
                helperText="Nombre de la meta asociada"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Periodo"
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                required
                helperText="Mes de la venta (YYYY-MM)"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="ID Presupuesto"
                value={budgetId}
                onChange={(e) => setBudgetId(e.target.value)}
                required
                placeholder="Ej: 12345"
                helperText="ID único del presupuesto"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Monto ($)"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                placeholder="Ej: 50000"
                helperText="Valor de la venta en pesos"
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? 'Guardando...' : editingSale ? 'Actualizar Venta' : 'Crear Venta'}
              </Button>
              {editingSale && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      {/* Lista de ventas existentes */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Ventas Registradas ({sales.length})
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Meta</TableCell>
                <TableCell>Periodo</TableCell>
                <TableCell>ID Presupuesto</TableCell>
                <TableCell align="right">Monto ($)</TableCell>
                <TableCell>Fecha Creación</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay ventas registradas
                  </TableCell>
                </TableRow>
              ) : (
                sales.map((sale) => (
                  <TableRow key={sale.id}>
                    <TableCell>{sale.meta || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={formatPeriod(sale.period)} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{sale.budgetId || '-'}</TableCell>
                    <TableCell align="right">
                      {sale.amount ? `$${sale.amount.toLocaleString('es-CL')}` : '-'}
                    </TableCell>
                    <TableCell>{formatDate(sale.createdAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(sale)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(sale.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageSales;
